import { ChevronIcon, CloseIcon, MenuIcon, SearchIcon } from "components/Icons";
import { ClariahLink } from "components/ClariahLink";
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from "body-scroll-lock";
import { DOWN, LARGE, EN, PAGE_TYPE } from "shared/constants";
import { Logo } from "components/Logo";
import { MaxWidth } from "components/MaxWidth";
import {
  OVERVIEW,
  SEARCH_ARIA_LABEL,
  MENU_ARIA_LABEL,
  CLOSE,
  SKIPLINK_CONTENT,
  SKIPLINK_NAV,
  SKIPLINK_SEARCH,
  SWITCH_LOCALE,
  translate,
} from "shared/translate";
import { SearchBar } from "components/SearchBar";
import { createInternalHref } from "shared/createInternalHref";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useScrollDirection } from "shared/useScrollDirection";
import { useWindowWidth } from "shared/useWindowWidth";
import cx from "classnames";
import FocusLock from "react-focus-lock";
import styles from "./Header.module.css";

const SEARCH_BUTTON_JS_CLASS = "js-search-button";
const MENU_BUTTON_JS_CLASS = "js-menu-button";

function mapNavigationItems(items) {
  return (items || []).map(({ item, label, subitems }) => ({
    href: item?.slug?.current || "",
    label,
    type: item?._type,
    ...(subitems?.length ? { subitems: mapNavigationItems(subitems) } : {}),
  }));
}

export const Header = ({ isDark, navigation, mirrorPath }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const headerRef = useRef();
  const router = useRouter();
  const scrollDirection = useScrollDirection();
  const windowWidth = useWindowWidth();

  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
  }, [router.asPath]);

  useEffect(() => {
    if (windowWidth >= 768) {
      setMenuOpen(false);
    }
  }, [windowWidth]);

  useEffect(() => clearAllBodyScrollLocks, []);

  useEffect(() => {
    if (menuOpen || searchOpen) {
      disableBodyScroll(headerRef.current);
    } else {
      enableBodyScroll(headerRef.current);
    }
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    function onEscape(e) {
      const isEscape =
        e.key === "Escape" || e.key === "Esc" || e.keyCode === 27;

      if (isEscape) {
        if (!searchQuery || searchQuery === "") {
          setSearchOpen(false);
          const searchButton = document.querySelector(
            `.${SEARCH_BUTTON_JS_CLASS}`
          );

          searchButton && searchButton.focus();
        } else {
          setSearchQuery("");
        }
      }
    }

    if (searchOpen) {
      window.addEventListener("keydown", onEscape, false);
    }

    return () => window.removeEventListener("keydown", onEscape);
  }, [menuOpen, searchOpen, searchQuery]);

  useEffect(() => {
    function onEscape(e) {
      const isEscape =
        e.key === "Escape" || e.key === "Esc" || e.keyCode === 27;

      if (isEscape) {
        setMenuOpen(false);

        const menuButton = document.querySelector(`.${MENU_BUTTON_JS_CLASS}`);

        menuButton && menuButton.focus();
      }
    }

    if (menuOpen) {
      window.addEventListener("keydown", onEscape, false);
    }

    return () => window.removeEventListener("keydown", onEscape);
  }, [menuOpen]);

  return (
    <>
      <SkipLinks setSearchOpen={setSearchOpen} />
      <FocusLock disabled={!menuOpen && !searchOpen}>
        <header
          tabIndex={windowWidth <= 768 ? "-1" : null}
          ref={headerRef}
          className={cx(styles.header, {
            [styles.isDark]: !scrollDirection && isDark && !searchOpen,
            [styles.menuOpen]: menuOpen,
            [styles.scrollHidden]: scrollDirection === DOWN && !menuOpen,
            [styles.scrolling]: !!scrollDirection,
          })}
        >
          {searchOpen ? (
            <SearchBar
              setSearchOpen={setSearchOpen}
              query={searchQuery}
              setQuery={setSearchQuery}
            />
          ) : (
            <NavBar
              isDark={!scrollDirection && isDark && !searchOpen}
              isDesktop={windowWidth >= 768}
              menuOpen={menuOpen}
              mirrorPath={mirrorPath}
              navigation={navigation}
              router={router}
              searchOpen={searchOpen}
              setMenuOpen={setMenuOpen}
              setSearchOpen={setSearchOpen}
            />
          )}
        </header>
        {searchOpen && (
          <div
            className={styles.searchBarBackground}
            onClick={() => setSearchOpen(false)}
          />
        )}
      </FocusLock>
    </>
  );
};

const NavBar = ({
  isDark,
  isDesktop,
  menuOpen,
  mirrorPath,
  navigation,
  router,
  searchOpen,
  setMenuOpen,
  setSearchOpen,
}) => {
  const { locale } = useRouter();
  const navigationItems = mapNavigationItems(navigation);

  return (
    <MaxWidth size={LARGE}>
      <div className={cx(styles.inner, { [styles.menuOpen]: menuOpen })}>
        <div className={styles.logoNavContainer}>
          <div className={styles.logoCloseContainer}>
            <ClariahLink
              className={styles.logo}
              type={PAGE_TYPE}
              href=""
              title="Homepage"
            >
              <Logo inverted={menuOpen || isDark} />
            </ClariahLink>
            <button
              className={cx(styles.closeButton, {
                [styles.menuOpen]: menuOpen,
              })}
              onClick={() => setMenuOpen(false)}
              aria-label={translate(CLOSE, locale)}
            >
              <CloseIcon />
            </button>
          </div>

          <nav
            id="nav"
            className={cx(styles.navigation, {
              [styles.menuOpen]: menuOpen,
            })}
          >
            <ul className={styles.navigationList}>
              {navigationItems.map((item) =>
                renderNavigationItem({ ...item, isDark, isDesktop, locale })
              )}
            </ul>
          </nav>
        </div>

        <div
          className={cx(styles.buttonsContainer, {
            [styles.menuOpen]: menuOpen,
          })}
        >
          {mirrorPath && (
            <LanguageSwitch
              isDark={isDark}
              locale={locale}
              menuOpen={menuOpen}
              mirrorPath={mirrorPath}
            />
          )}
          <button
            aria-label={translate(SEARCH_ARIA_LABEL, locale)}
            className={cx(styles.button, SEARCH_BUTTON_JS_CLASS, {
              [styles.isDark]: isDark,
            })}
            onClick={() => setSearchOpen(!searchOpen)}
          >
            <SearchIcon />
          </button>
          <button
            aria-label={translate(MENU_ARIA_LABEL, locale)}
            className={cx(
              styles.button,
              styles.menuButton,
              MENU_BUTTON_JS_CLASS,
              { [styles.isDark]: isDark }
            )}
            onClick={() => setMenuOpen(true)}
          >
            <MenuIcon />
          </button>
        </div>
      </div>
    </MaxWidth>
  );

  function renderNavigationItem({
    href,
    isDark,
    isDesktop,
    label,
    locale,
    subitems,
    type,
  }) {
    const [subItemsOpen, setSubItemsOpen] = useState(false);

    const internalHref = createInternalHref({ href, locale, type });
    const internalHrefWithoutSlash = internalHref.endsWith("/")
      ? internalHref.slice(0, -1)
      : internalHref;

    const isActive =
      internalHrefWithoutSlash === router.asPath ||
      router.asPath.indexOf(internalHrefWithoutSlash) === 0;

    const hasSubItems = subitems?.length > 0;
    const subItemsWithOverview = hasSubItems
      ? isDesktop
        ? subitems
        : [{ type, href, label: translate(OVERVIEW, locale) }, ...subitems]
      : null;

    const LinkElement = !isDesktop && hasSubItems ? "button" : ClariahLink;
    const linkElementProps =
      !isDesktop && hasSubItems
        ? {
            className: subItemsOpen ? styles.subItemsOpen : null,
            onClick: () => setSubItemsOpen(!subItemsOpen),
          }
        : { href, type };

    return (
      <li
        key={`${label}/${href}`}
        className={cx(styles.navigationItem, {
          [styles.isActive]: isActive,
          [styles.isDark]: isDark,
        })}
      >
        <LinkElement {...linkElementProps}>
          <span>{label}</span>
          {hasSubItems && <ChevronIcon />}
        </LinkElement>

        {hasSubItems && (
          <ul
            className={cx(styles.navigationSubList, {
              [styles.subItemsOpen]: subItemsOpen,
            })}
          >
            {subItemsWithOverview.map(renderSubNavigationItem)}
          </ul>
        )}
      </li>
    );
  }

  function renderSubNavigationItem({ type, href, label }) {
    const internalHref = createInternalHref({ href, locale, type });
    const internalHrefWithoutSlash = internalHref.endsWith("/")
      ? internalHref.slice(0, -1)
      : internalHref;

    const isActive =
      internalHrefWithoutSlash === router.asPath ||
      router.asPath.indexOf(internalHrefWithoutSlash) === 0;

    return (
      <li
        key={`${label}/${href}`}
        className={cx(styles.subNavItem, { [styles.isActive]: isActive })}
      >
        <ClariahLink href={href} type={type}>
          {label}
        </ClariahLink>
      </li>
    );
  }
};

const LanguageSwitch = ({ isDark, locale, mirrorPath, menuOpen }) => {
  const router = useRouter();

  function onClick(e) {
    e.preventDefault();

    if (window.gtag) {
      window.gtag("onLanguageSwitchClick");
    }

    router.push(mirrorPath, mirrorPath, { locale: false });
  }

  return (
    <div
      className={cx(styles.languageSwitch, {
        [styles.isDark]: isDark,
        [styles.leftActive]: locale === EN,
        [styles.menuOpen]: menuOpen,
      })}
    >
      <span>EN</span>
      <a
        href={mirrorPath}
        onClick={onClick}
        aria-label={translate(SWITCH_LOCALE, locale)}
        className={styles.languageSwitchButton}
      >
        <span />
      </a>
      <span>NL</span>
    </div>
  );
};

const SkipLinks = ({ setSearchOpen }) => {
  const { locale } = useRouter();

  function onSearchClick(e) {
    e.preventDefault();
    setSearchOpen(true);
  }

  return (
    <ul className={styles.skipLinks}>
      <li>
        <a href="#content">{translate(SKIPLINK_CONTENT, locale)}</a>
      </li>
      <li>
        <a href="#nav">{translate(SKIPLINK_NAV, locale)}</a>
      </li>
      <li>
        <button type="button" onClick={onSearchClick}>
          {translate(SKIPLINK_SEARCH, locale)}
        </button>
      </li>
    </ul>
  );
};
